import { Fill, Stroke, Style, Text, Circle as CircleStyle } from 'ol/style';
import Icon from 'ol/style/Icon';

/**
 * Утилита для получения текста из feature
 */
function getText(feature, field = 'label') {
  return feature.get(field) || '';
}

/**
 * Стиль для слоя landspol
 */
export function landspolStyle(feature, resolution) {
  // Обводка (белая, широкая)
  const outlineStyle = new Style({
    stroke: new Stroke({
      color: [255, 255, 255, 1],
      width: 5
    })
  });

  // Определение цвета по land_type
  let color = [0, 0, 0, 1]; // черный по умолчанию
  let fillcolor = [255, 255, 255, 0.1]; // белый с прозрачностью
  const properties = feature.getProperties();
  const disabled = properties.source === 'dispol';

  switch (properties.land_type) {
    case -2:
    case -3:
      color = [255, 0, 0, 1]; // красный
      break;
    case 0:
      color = [0, 255, 0, 1]; // зеленый
      break;
    case 1:
    case 2:
      color = [0, 50, 155, 1]; // синий
      break;
  }

  if (disabled) {
    fillcolor = [0, 0, 0, 0.3];
  }

  // Основной стиль с заливкой, обводкой и текстом
  const mainStyle = new Style({
    fill: new Fill({
      color: fillcolor
    }),
    stroke: new Stroke({
      color: color,
      width: disabled ? 1 : 3
    }),
    text: new Text({
      text: getText(feature, 'label'),
      stroke: new Stroke({
        color: [255, 255, 255, 1],
        width: 3
      }),
      fill: new Fill({
        color: color
      }),
      overflow: !disabled && resolution < 50
    })
  });

  return [outlineStyle, mainStyle];
}

/**
 * Стиль для слоя zonespol (используется также для zninspol)
 */
export function zonespolStyle(feature, resolution) {
  let color = [100, 255, 100, 0.5]; // зеленый по умолчанию
  if (feature.getProperties().zone_type === 2) {
    color = [100, 100, 255, 0.5]; // синий для zone_type=2
  }

  const shadow = [100, 100, 100];
  const outlineStyle = new Style({
    stroke: new Stroke({
      color: shadow,
      width: 1
    })
  });

  const mainStyle = new Style({
    fill: new Fill({
      color: color
    }),
    stroke: new Stroke({
      color: color,
      width: 1
    }),
    text: new Text({
      text: getText(feature, 'label'),
      stroke: new Stroke({
        color: shadow,
        width: 1
      }),
      fill: new Fill({
        color: color
      }),
      overflow: resolution > 100
    })
  });

  return [outlineStyle, mainStyle];
}

/**
 * Стиль для слоя oopt
 */
export function ooptStyle(feature, resolution) {
  const properties = feature.getProperties();
  const name = (properties.name || '').toLowerCase();
  
  let color = 'rgba(0,0,0,0)';
  let lineDash = null;

  switch (name[0]) {
    case 'ф':
      color = 'rgba(255,0,0,1)';
      break;
    case 'р':
      color = 'rgba(255,100,100,1)';
      break;
    case 'м':
      color = 'rgba(255,100,0,1)';
      break;
  }

  if (name.indexOf('охранная зона') !== -1) {
    lineDash = [10, 5];
  }

  const mainStyle = new Style({
    stroke: new Stroke({
      color: color,
      width: 2,
      lineDash: lineDash
    }),
    fill: new Fill({
      color: 'rgba(0,0,0,0)'
    }),
    text: new Text({
      text: getText(feature, 'label'),
      stroke: new Stroke({
        color: [255, 255, 255, 1],
        width: 3
      }),
      fill: new Fill({
        color: 'black'
      }),
      overflow: resolution < 50
    })
  });

  return [mainStyle];
}

/**
 * Стиль для слоя admpol
 */
export function admpolStyle(feature, resolution) {
  const textStyle = new Style({
    text: new Text({
      text: getText(feature, 'label'),
      offsetX: 10,
      offsetY: -10,
      stroke: new Stroke({
        color: [0, 0, 0, 1],
        width: 3
      })
    })
  });

  const mainStyle = new Style({
    fill: new Fill({
      color: 'rgba(255, 255, 255, 0.1)'
    }),
    stroke: new Stroke({
      color: [255, 0, 0, 1],
      lineDash: [10, 5, 1, 5],
      width: 1
    }),
    text: new Text({
      text: getText(feature, 'label'),
      offsetX: 10,
      offsetY: -10,
      stroke: new Stroke({
        color: [255, 255, 255, 1],
        width: 2
      })
    })
  });

  return [textStyle, mainStyle];
}

/**
 * Стиль для слоя dtplin
 */
export function dtplinStyle(feature, resolution) {
  const mainStyle = new Style({
    stroke: new Stroke({
      color: [255, 50, 50, 1],
      width: 3
    })
  });

  return [mainStyle];
}

/**
 * Стиль для слоя dtppnt
 */
export function dtppntStyle(feature, resolution) {
  const properties = feature.getProperties();
  const specId = properties.spec_id || properties.meeting_spec;
  const radius = 4;

  const colors = {
    8801: [144, 255, 144, 1],
    8804: [224, 255, 50, 1],
    8818: [230, 170, 20, 1],
    8800: [245, 105, 105, 1],
    8842: [255, 105, 215, 1],
    8805: [125, 125, 125, 1]
  };

  const outlineStyle = new Style({
    image: new CircleStyle({
      radius: radius + 1,
      fill: new Fill({
        color: [0, 0, 0, 1]
      })
    })
  });

  const mainStyle = new Style({
    image: new CircleStyle({
      radius: radius,
      fill: new Fill({
        color: colors[specId] || [100, 100, 255, 1]
      })
    })
  });

  return [outlineStyle, mainStyle];
}

/**
 * Проверяет, попадает ли feature (пункт выдачи) под фильтр
 * @param {ol.Feature} feature
 * @param {{region?: string, users?: number|string, ecos?: number|string, lands?: number|string}} filters
 * @returns {boolean}
 */
function featureMatchesPickupFilter(feature, filters) {
  if (!filters || (!filters.region && !filters.users && !filters.ecos && !filters.lands)) return true;

  const economyIds = (feature.get('economy_ids') || '')
    .split(',')
    .filter(Boolean)
    .map((s) => parseInt(s, 10))
    .filter((n) => !isNaN(n));
  const huntuserIds = (feature.get('huntuser_ids') || '')
    .split(',')
    .filter(Boolean)
    .map((s) => parseInt(s, 10))
    .filter((n) => !isNaN(n));
  const regionOktmos = (feature.get('region_oktmos') || '')
    .split(',')
    .filter(Boolean)
    .map((s) => String(s).trim());

  if (filters.region != null && filters.region !== '') {
    const r = String(filters.region).trim();
    if (r && !regionOktmos.includes(r)) return false;
  }
  if (filters.users != null) {
    const u = parseInt(filters.users, 10);
    if (!isNaN(u) && !huntuserIds.includes(u)) return false;
  }
  if (filters.ecos != null) {
    const e = parseInt(filters.ecos, 10);
    if (!isNaN(e) && !economyIds.includes(e)) return false;
  }
  return true;
}

/**
 * Стиль для слоя pickuppnt (пункты выдачи) — иконка домика
 * @param {Object} [filters] — {users, ecos, lands} для подсветки попадающих под фильтр
 */
export function pickuppntStyle(feature, resolution, filters) {
  const matches = featureMatchesPickupFilter(feature, filters);
  const opacity = matches ? 1 : 0.3;
  // Попадающие под фильтр — оранжевые, остальные — бирюзовые
  const fill = matches ? 'rgba(255,165,0,0.8)' : 'rgba(64,224,208,0.7)';
  const stroke = matches ? 'rgba(210,105,30,1)' : 'rgba(0,139,139,0.9)';

  const houseSvg = 'data:image/svg+xml;utf8,' + encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">' +
    `<path fill="${fill}" stroke="${stroke}" stroke-width="2" d="M12 3L2 12h3v9h6v-6h2v6h6v-9h3L12 3z"/>` +
    '</svg>'
  );
  return new Style({
    image: new Icon({
      src: houseSvg,
      anchor: [0.5, 1],
      scale: matches ? 0.9 : 0.7,
      opacity
    })
  });
}

/**
 * Возвращает style-функцию для pickuppnt с привязкой к filters
 */
export function createPickuppntStyle(filters) {
  return (feature, resolution) => pickuppntStyle(feature, resolution, filters);
}
